//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import * as instagramAudienceMetricsApi from '@apis/gateway-instagram/instagramAudienceMetricsApi';
import { IInstagramWeekAudienceMetricsResponse } from '@apis/gateway-instagram/instagramAudienceMetricsModel';
import { IInstagramAudienceDailyMetrics } from '@instagram-account/models/IInstagramAudienceDailyMetrics';
import { parseInstagramDailyAudienceMetricsResponse } from '@instagram-account/redux/audiencev2/instagramAudienceUtils';
import { IInstagramSummaryState } from '@instagram-account/redux/summary/instagramSummaryModel';
import DateString from '@models/DateString';
import TimeZoneId from '@models/TimeZoneId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';

const initialState: IInstagramSummaryState = {
  errors: {},
  flags: {
    isFetchingDailyAudienceMetrics: false,
    isFetchingWeekAudienceMetrics: false,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramSummary,
  initialState,
  reducers: {
    requestDailyAudienceMetrics(state) {
      state.flags.isFetchingDailyAudienceMetrics = true;
      state.errors.fetchDailyAudienceMetricsError = undefined;
    },
    receiveDailyAudienceMetricsSucceeded(
      state,
      action: PayloadAction<IInstagramAudienceDailyMetrics>,
    ) {
      state.flags.isFetchingDailyAudienceMetrics = false;
      state.dailyAudienceMetrics = action.payload;
    },
    receiveDailyAudienceMetricsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingDailyAudienceMetrics = false;
      state.errors.fetchDailyAudienceMetricsError = action.payload;
    },
    requestWeekAudienceMetrics(state) {
      state.flags.isFetchingWeekAudienceMetrics = true;
      state.errors.fetchWeekAudienceMetricsError = undefined;
    },
    receiveWeekAudienceMetricsSucceeded(
      state,
      action: PayloadAction<IInstagramWeekAudienceMetricsResponse>,
    ) {
      state.flags.isFetchingWeekAudienceMetrics = false;
      state.weekAudienceMetrics = action.payload.weekMetrics;
      state.weekComparisonAudienceMetrics =
        action.payload.weekComparisonMetrics;
    },
    receiveWeekAudienceMetricsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingWeekAudienceMetrics = false;
      state.errors.fetchWeekAudienceMetricsError = action.payload;
    },
  },
});

/**
 * Fetches the last week of daily audience metrics
 */
export const fetchLastWeekOfDailyAudienceMetrics = createThunk(
  () => async (dispatch, getState) => {
    const defaultDateRange =
      getState().instagramUserStats.summaryDefaultDateRange;

    const { isFetchingDailyAudienceMetrics } =
      getState().instagram.summary.flags;
    if (isFetchingDailyAudienceMetrics) {
      return;
    }

    dispatch(actions.requestDailyAudienceMetrics());
    try {
      let startDate: DateString;
      let endDate: DateString;
      if (isDefined(defaultDateRange)) {
        const dateRange = DateString.fromUnisonDateRange(defaultDateRange);
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
      } else {
        startDate = DateString.addDays(
          DateString.getToday(TimeZoneId.AmericaLosAngeles),
          -6,
        );
        endDate = DateString.getToday(TimeZoneId.AmericaLosAngeles);
      }

      const dailyAudienceMetricsResponse =
        await instagramAudienceMetricsApi.getDailyAudienceMetrics({
          startTime: startDate,
          endTime: endDate,
        });

      const dailyAudienceMetrics = parseInstagramDailyAudienceMetricsResponse(
        dailyAudienceMetricsResponse,
      );

      dispatch(
        actions.receiveDailyAudienceMetricsSucceeded(dailyAudienceMetrics),
      );
    } catch (_e) {
      dispatch(
        actions.receiveDailyAudienceMetricsFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

/*
 * Fetches the latest weekly audience metrics
 */
export const fetchLatestWeeklyAudienceMetrics = createThunk(
  () => async (dispatch, getState) => {
    const { isFetchingWeekAudienceMetrics } =
      getState().instagram.summary.flags;
    if (isFetchingWeekAudienceMetrics) {
      return;
    }

    dispatch(actions.requestWeekAudienceMetrics());
    try {
      const weekAudienceMetricsResponse =
        await instagramAudienceMetricsApi.getLatestWeeklyMetrics();

      if (weekAudienceMetricsResponse.weekMetrics) {
        dispatch(
          actions.receiveWeekAudienceMetricsSucceeded(
            weekAudienceMetricsResponse,
          ),
        );
      } else {
        dispatch(
          actions.receiveWeekAudienceMetricsFailed(
            'Week metrics are unavailable',
          ),
        );
      }
    } catch (_e) {
      dispatch(
        actions.receiveWeekAudienceMetricsFailed(
          'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
        ),
      );
    }
  },
);

export default reducer;
